/* eslint-disable max-len */
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Snackbar } from '@mui/material';
import { SeelEmail, FullpageLoading, RadioPart } from '@components';
import CloseIcon from '@mui/icons-material/Close';
import { useEffectOnce } from '@hooks';
import useConnectionContext from '@hooks/useConnectionContext';
import { useNavigate } from 'react-router-dom';
import request from '@api';
import { getRASettings } from '@api/getSettings';
import SingleHeader from '@components/SingleHeader';
import classes from './index.module.css';
// import { MOCK_DATA, MOCK_SUBMIT } from './mock';
import CollectionPanel from './CollectionPanel';

const SETTINGS = {
  title: 'Settings', subTitle: 'to make changes',
};
const TOAST_TEXT = {
  suc: 'Settings updated',
  notAuth: 'Not Authorized',
  err: 'Something went wrong, Please try again later',
};
const getEligibleText = ({
  categories = [], collectionNames, discountThreshold, tag,
}) => {
  let text = '-';
  // 优先级4>3>2>1
  const arr = categories.sort((a, b) => b - a);
  const renderNewEligible = (type) => {
    console.log(arr);
    const eligibleArr = [];
    if (collectionNames?.length) {
      eligibleArr.push(`collection is in ${collectionNames?.join(';')}`);
    }
    if (discountThreshold) {
      eligibleArr.push(` Discount ≥ ${discountThreshold}%`);
    }
    if (tag) {
      eligibleArr.push('Product tag is ra-include');
    }

    return (
      <div>
        <div>
          {type === '5' ? 'All ' : 'Any '}
          of the following conditions are met:
        </div>
        <div>
          {eligibleArr.map((item, index) => (
            <CollectionPanel key={item} title={`Condition ${index + 1}`} desc={item} />

          ))}
        </div>
      </div>
    );
  };

  switch (arr[0]) {
    case 1: text = `Selected products with product tag ${tag}`; break;
    case 2: text = `Selected products with discount >= ${discountThreshold}%`; break;
    case 3: text = `Selected products with collections in ${collectionNames.join('、')}.`; break;
    case 4: text = 'All products'; break;
    case 5:
    case 6:
      text = renderNewEligible(arr[0]); break;
    default: text = '-';
  }
  return text;
};

const getMethod = ({
  returnToMerchant, returnShippingFee, buybackFeeRate,
}, isShowShoppingFee) => {
  const SUBTITLE = returnToMerchant ? 'Return to Merchant' : 'Return to Seel';
  const feeRate = (buybackFeeRate || 0) * 100;
  return (
    <div>
      <div className={classes.method_title}>
        {returnToMerchant ? 'Return to Merchant' : 'Return to Seel'}
      </div>
      <div>
        {`With “${SUBTITLE}” customers will submit their return request `}
        {returnToMerchant ? (
          <span>
            to you directly. Seel will then reimburse you for your refund to customers with orders covered by Return Assurance. The reimbursement is API-enabled, so no effort is required from you. Please note that there will be a
            <span className={classes.bold}>
              {` ${feeRate}% `}
            </span>
            buyback fee that will be deducted from the reimbursement.
          </span>
        ) : (
          <>
            via
            <a className={classes.color} href="https://resolve.seel.com/" target="_blank" rel="noreferrer"> Seel Resolution Center  </a>
            for any order covered by Return Assurance. Seel will then generate the return shipping label for them to send return items to Seel’s warehouse. Please note that
            {isShowShoppingFee ? (
              <span>
                {' '}
                there will be a
                <span className={classes.bold}>
                  {` $${returnShippingFee || '_'} shipping label fee `}
                </span>
                {'that '}
              </span>
            ) : ' return shipping fee '}
            will be deducted from your customers’ refund.
          </>
        )}
      </div>
    </div>
  );
};

export default function RASettings() {
  const [settingData, setSettingData] = useState({});
  const [radioValue, setRadioValue] = useState('1');
  const [toast, setToast] = useState({ show: false, message: '' });
  const [pageLoading, setPageLoading] = useState(true);
  const navigate = useNavigate();
  const { store = {} } = useConnectionContext();
  const { name: platform, info } = store;
  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  // 是否展示具体的shippingfee，spo等不展示
  const isShowShoppingFee = !(['Gently', 'Curtsy'].includes(platform) || ['shoppremiumoutlets.myshopify.com', 'sspotest.myshopify.com'].includes(info));

  const getData = async () => {
    try {
      const { code, data = {} } = await getRASettings();
      if (code === 0) {
        setSettingData(data);
        const { defaultOn } = data?.widgetVO || {};
        setRadioValue(defaultOn ? '1' : '2');
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const { code } = await request('update-merchant-ra-settings', {
        method: 'POST',
        body: {
          defaultOn: radioValue === '1',
        },
      });
      // const { code, data = {} } = MOCK_SUBMIT;
      if (code === 0) {
        setToast({ show: true, message: TOAST_TEXT.suc });
        getData();
      }
    } catch (e) {
      console.log(e);
      const { originResponse: { status } = {} } = e;
      setToast({
        show: true,
        message: status === 401 ? TOAST_TEXT.notAuth : TOAST_TEXT.err,
      });
    }
    setLoading(false);
    setIsDisabled(true);
  };

  const handleToastClose = () => {
    setToast({ show: false });
  };
  const radioOnChange = (e) => {
    setRadioValue(e.target.value);
    setIsDisabled((settingData.widgetVO?.defaultOn ? '1' : '2') === e.target.value);
  };

  const getTextList = ({
    returnMethodVO = {},
    eligibleProductsVO = {},
    returnWindowVO: { coverageDays } = {},
    widgetVO: { defaultOn } = {},
  }) => ([
    {
      title: 'RETURN METHOD',
      component: getMethod(returnMethodVO, isShowShoppingFee),

    }, {
      title: 'RETURN WINDOW',
      component: coverageDays ? (
        <div>
          Returns must be created within
          <span className={classes.bold}>{` ${coverageDays} `}</span>
          days upon delivery.
        </div>
      ) : '-',
    }, {
      title: 'ELIGIBLE PRODUCTS',
      component: getEligibleText(eligibleProductsVO),
    }, {
      title: 'WIDGET SETTINGS',
      // component: (
      //   <div>
      //     Return Option will be
      //     <span className={classes.bold}>{defaultOn ? ' On ' : ' Off '}</span>
      //     by default. Buyers can choose to
      //     {defaultOn ? ' opt-out.' : ' opt-in.'}
      //   </div>
      // ),
      component: (platform !== 'Shopify') ? (
        <div>
          Return Option will be

          {' '}
          <span className={classes.bold}>{defaultOn ? ' On ' : ' Off '}</span>
          by default. Buyers can choose to

          {' '}
          {defaultOn ? ' opt-out.' : ' opt-in.'}

          {' '}
        </div>
      ) : <RadioPart radioValue={radioValue} radioOnChange={radioOnChange} />,
    },
  ]);

  const handleClose = () => {
    navigate(-1);
  };
  useEffectOnce(async () => {
    setPageLoading(true);
    await getData();
    setPageLoading(false);
  }, []);

  const textList = getTextList(settingData);

  if (pageLoading) {
    return <FullpageLoading />;
  }
  return (
    <div className={classes.settings}>
      <SingleHeader title="Return Assurance Settings" handleClose={handleClose} />
      <div className={classes.content}>
        <div className={classes.title}>
          {SETTINGS.title}
        </div>
        <div className={classes.sub_title}>
          <SeelEmail text="Contact support" />
          {' '}
          {SETTINGS.subTitle}
        </div>
        <div className={classes.settings_content}>
          <div className={classes.panel}>
            Return Assurance is
            <span className={classes.enabled}> enabled</span>
          </div>
          <div className={classes.panel}>
            {textList.map((setting) => (
              <div className={classes.setting} key={setting.title}>
                <div className={classes.setting_title}>
                  {setting.title}
                </div>
                <div className={classes.setting_desc}>
                  {setting.component}
                </div>
              </div>
            ))}
          </div>
          {platform === 'Shopify' && (
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: '#645AFF',
                '&:hover': {
                  backgroundColor: '#5950DF',
                },
              }}
              className={classes.button}
              disabled={isDisabled}
              loading={loading}
            >
              Save
            </LoadingButton>
          )}
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            open={toast.show}
            onClose={handleToastClose}
            autoHideDuration={3000}
            message={toast.message}
            action={(
              <CloseIcon
                onClick={handleToastClose}
              />
            )}
            sx={{
              '& .MuiPaper-root': {
                height: '60PX',
                fontSize: '20PX',
              },

            }}
          />
        </div>
      </div>
    </div>
  );
}
